import { For, Match, Show, Switch, createEffect, createMemo, createResource, createSignal, onCleanup, onMount } from "solid-js";
import { ColorPanel } from "./components/ColorPanel";
import { categoriesForCatalogQuickFilter, categoryDisplayName, latestCategory, sortEntriesByNewestCategory, type CatalogQuickFilter } from "./categories";
import { CompareDrawer } from "./components/CompareDrawer";
import { CoordCard } from "./components/CoordCard";
import { LookbookCard } from "./components/LookbookCard";
import { FilterPanel } from "./components/FilterPanel";
import { HelpPanel } from "./components/HelpPanel";
import { SettingsPanel } from "./components/SettingsPanel";
import { ImagePreviewModal, type ImagePreviewTarget } from "./components/ImagePreviewModal";
import { QrRegistrationModal, type QrRegistrationTarget } from "./components/QrRegistrationModal";
import { QrModal, type QrModalTarget } from "./components/QrModal";
import { SendFailureModal, type SendFailureTarget } from "./components/SendFailureModal";
import { VirtualGrid } from "./components/VirtualGrid";
import { imageUrl, loadEntries, normalizeTerm } from "./data";
import { appendUniqueColors, hslToHex } from "./color";
import { CoordList } from "./features/catalog/list/CoordList";
import { FriendCodeList } from "./features/friends/FriendCodeList";
import { FriendCoordinateEditor } from "./features/friends/FriendCoordinateEditor";
import { FriendRegistrationModal } from "./features/friends/FriendRegistrationModal";
import { buildFriendCodeListItems, catalogCoordinateId, friendDisplayEntry } from "./features/friends/selectors";
import type { FriendCode, FriendCodeListItem, FriendCoordinateOption, FriendCoordinateReference } from "./features/friends/types";
import { MixCoordinateList } from "./features/mixes/MixCoordinateList";
import { buildMixEntries } from "./features/mixes/selectors";
import type { MixCoordinateRegistration } from "./features/mixes/types";
import { buildCatalogListRows, catalogItemDisplayStatus, findCatalogListRow, isCatalogListRowOwned, ownershipRequestIds, qrRequestTarget } from "./features/catalog/selectors";
import { loadViewPreferences, saveViewPreferences } from "./features/preferences/storage";
import type { ViewMode } from "./features/preferences/types";
import { loadDataSource, saveDataSource } from "./services/localStorage";
import { isDataSourceConfigured, type UserDataSource } from "./services/dataSource";
import { postItemOwnership, postItemQr } from "./services/aipriDb";
import { fetchFriendCodes, postFriendCode, updateFriendCoordinate } from "./services/friends";
import { fetchMixCoordinates, postMixCoordinate } from "./services/mixes";
import { describeSendFailure } from "./services/apiResponse";
import { DEFAULT_COLOR_FILTERS, buildFacets, colorFiltersActive, colorMatchScore, matchesEntry } from "./search";
import { styles } from "./styles";
import { GROUP_ORDER, type ColorFilters, type CompareGroup, type CoordEntry, type CoordItem, type LoadResult, type Selection } from "./types";

const emptySelection = (): Selection => ({
  アクセ: [],
  トップス: [],
  ワンピ: [],
  ボトムス: [],
  シューズ: [],
});

const defaultColorFilters = (): ColorFilters => ({
  ...DEFAULT_COLOR_FILTERS,
  hslColors: [],
  targets: [...DEFAULT_COLOR_FILTERS.targets],
});

function toggleSet(current: ReadonlySet<string>, value: string): Set<string> {
  const next = new Set(current);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

function randomOrderValue(value: string, seed: number): number {
  let hash = seed | 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 0x45d9f3b);
  }
  return (hash ^ (hash >>> 16)) >>> 0;
}

function randomizeEntries(entries: CoordEntry[], seed: number): CoordEntry[] {
  return entries
    .map((entry, originalIndex) => ({ entry, originalIndex, order: randomOrderValue(entry.key, seed) }))
    .sort((left, right) => left.order - right.order || left.originalIndex - right.originalIndex)
    .map(({ entry }) => entry);
}

function ownershipItemKey(item: CoordItem): string {
  return `${item.id}\n${item.url}`;
}

function catalogItemModalTitle(entry: CoordEntry, item: CoordItem): string {
  const row = findCatalogListRow(entry, item);
  if (row?.kind === "full") return `${entry.name}（フルコーデ）`;
  if (row?.kind === "set") return `${entry.name}（セット）`;
  return item.name !== entry.name ? item.name : `${item.name} ${item.term}`;
}

function restoreWindowScroll(left: number, top: number): void {
  if (window.scrollX === left && window.scrollY === top) return;
  const previousBehavior = document.documentElement.style.scrollBehavior;
  document.documentElement.style.scrollBehavior = "auto";
  window.scrollTo(left, top);
  document.documentElement.style.scrollBehavior = previousBehavior;
}

function visibleCatalogEntryKey(): string | null {
  const headerBottom = document.querySelector("header")?.getBoundingClientRect().bottom ?? 0;
  const visible = [...document.querySelectorAll<HTMLElement>("[data-catalog-entry-key]")]
    .map((element) => ({ element, rect: element.getBoundingClientRect() }))
    .filter(({ rect }) => rect.bottom > headerBottom && rect.top < window.innerHeight)
    .sort((left, right) => Math.abs(left.rect.top - headerBottom) - Math.abs(right.rect.top - headerBottom));
  return visible[0]?.element.dataset.catalogEntryKey ?? null;
}

function scrollToCatalogEntry(key: string): void {
  const element = [...document.querySelectorAll<HTMLElement>("[data-catalog-entry-key]")]
    .find((candidate) => candidate.dataset.catalogEntryKey === key);
  if (!element) return;
  const headerHeight = document.querySelector("header")?.getBoundingClientRect().height ?? 0;
  window.scrollTo({
    top: Math.max(0, window.scrollY + element.getBoundingClientRect().top - headerHeight - 8),
    behavior: "smooth",
  });
}

function coordinateMetadata(entry: CoordEntry): NonNullable<ImagePreviewTarget["metadata"]> {
  return [
    { label: "★", value: entry.star || "-" },
    { label: "ブランド", value: entry.brand || "-" },
    { label: "カテゴリ", value: categoryDisplayName(entry.category) },
    ...(entry.series === "primagi" ? [{
      label: "使用可否",
      value: entry.available === true ? "使用可能" : entry.available === false ? "使用不可" : "不明",
    }] : []),
    {
      label: "ID",
      value: entry.items.map((item) => `${item.term}: ${item.id}`).join(" / ") || "-",
      wide: true,
    },
  ];
}

type AppRoute = "catalog" | "mixes" | "friends";

function routeFromPath(pathname: string): AppRoute {
  if (/(?:^|\/)friends\/?$/.test(pathname)) return "friends";
  return /(?:^|\/)mixes\/?$/.test(pathname) ? "mixes" : "catalog";
}

function routeFromLocation(): AppRoute {
  const hashPath = window.location.hash.replace(/^#/, "");
  if (/(?:^|\/)(?:friends|mixes)\/?$/.test(hashPath)) return routeFromPath(hashPath);
  return routeFromPath(window.location.pathname);
}

const appDocumentPath = window.location.pathname.replace(/\/(?:friends|mixes)\/?$/, "/");

function routePath(route: AppRoute): string {
  const url = new URL(window.location.href);
  url.pathname = appDocumentPath;
  url.hash = route === "catalog" ? "" : `/${route}`;
  return `${url.pathname}${url.search}${url.hash}`;
}

export default function App() {
  const [route, setRoute] = createSignal<AppRoute>(routeFromLocation());
  const [dataSource, setDataSource] = createSignal(loadDataSource());
  const storageConfigured = () => isDataSourceConfigured(dataSource());
  const [preferences, setPreferences] = createSignal(loadViewPreferences());
  const itemQrVisible = () => storageConfigured() && !preferences().hideCoordinateQr;
  const [result, { mutate, refetch }] = createResource<LoadResult, UserDataSource>(
    dataSource,
    (source, info) => loadEntries(source, {
      refreshStatuses: Boolean(info.refetching),
    }),
  );
  const [mixes, { mutate: mutateMixes, refetch: refetchMixes }] = createResource(
    dataSource,
    fetchMixCoordinates,
  );
  const [friendCodes, { mutate: mutateFriendCodes, refetch: refetchFriendCodes }] = createResource(
    () => storageConfigured() ? dataSource() : null,
    fetchFriendCodes,
  );
  const [queryInput, setQueryInput] = createSignal("");
  const [query, setQuery] = createSignal("");
  const [brands, setBrands] = createSignal<ReadonlySet<string>>(new Set());
  const [terms, setTerms] = createSignal<ReadonlySet<string>>(new Set());
  const [categories, setCategories] = createSignal<ReadonlySet<string>>(new Set());
  const [defaultCategoryApplied, setDefaultCategoryApplied] = createSignal(false);
  const [randomOrder, setRandomOrder] = createSignal(false);
  const [randomSeed, setRandomSeed] = createSignal(Date.now());
  const [colorFilters, setColorFilters] = createSignal<ColorFilters>(defaultColorFilters());
  const [pendingHeaderColorRemoval, setPendingHeaderColorRemoval] = createSignal<string | null>(null);
  const [compactViewSwitcherVisible, setCompactViewSwitcherVisible] = createSignal(false);
  const [compactViewMenuOpen, setCompactViewMenuOpen] = createSignal(false);
  const [listScrolled, setListScrolled] = createSignal(false);
  const [friendQuery, setFriendQuery] = createSignal("");
  const [selection, setSelection] = createSignal<Selection>(emptySelection());
  const [drawerOpen, setDrawerOpen] = createSignal(false);
  const [searchPanel, setSearchPanel] = createSignal<"data" | "color" | "help" | "settings" | null>(null);
  const [previewTargets, setPreviewTargets] = createSignal<ImagePreviewTarget[]>([]);
  const pushPreviewTarget = (target: ImagePreviewTarget) => setPreviewTargets((current) => [...current, target]);
  const popPreviewTarget = () => setPreviewTargets((current) => current.slice(0, -1));
  const [pendingOwnershipItems, setPendingOwnershipItems] = createSignal<ReadonlySet<string>>(new Set());
  const [sendFailures, setSendFailures] = createSignal<SendFailureTarget[]>([]);
  const [qrRegistration, setQrRegistration] = createSignal<{
    entry: CoordEntry;
    item: CoordItem;
    modal: QrRegistrationTarget;
  } | null>(null);
  const [qrDisplayTarget, setQrDisplayTarget] = createSignal<QrModalTarget | null>(null);
  const [friendRegistrationOpen, setFriendRegistrationOpen] = createSignal(false);
  const [editingFriend, setEditingFriend] = createSignal<FriendCode | null>(null);
  const [domPending, setDomPending] = createSignal(true);
  const reportSendFailure = (operation: string, error: unknown) => {
    setSendFailures((current) => [...current, { operation, ...describeSendFailure(error) }]);
  };
  const sendInBackground = <T,>(
    operation: string,
    request: () => Promise<T>,
    handlers: {
      onSuccess?: (value: T) => void;
      onFailure?: (error: unknown) => void;
      onSettled?: () => void;
    } = {},
  ) => {
    void Promise.resolve()
      .then(request)
      .then(
        (value) => handlers.onSuccess?.(value),
        (error: unknown) => {
          reportSendFailure(operation, error);
          handlers.onFailure?.(error);
        },
      )
      .finally(() => handlers.onSettled?.());
  };
  const appBusy = () => result.loading
    || mixes.loading
    || friendCodes.loading
    || domPending();

  createEffect(() => {
    if (!itemQrVisible()) {
      setQrRegistration(null);
      setQrDisplayTarget(null);
    }
  });

  createEffect(() => {
    const value = queryInput();
    const timer = window.setTimeout(() => setQuery(value), 300);
    onCleanup(() => window.clearTimeout(timer));
  });

  createEffect(() => {
    const pending = pendingHeaderColorRemoval();
    if (pending && !colorFilters().hslColors.some((color) => hslToHex(color) === pending)) {
      setPendingHeaderColorRemoval(null);
    }
  });

  onMount(() => {
    let compactViewFrame: number | undefined;
    const updateCompactViewSwitcher = () => {
      compactViewFrame = undefined;
      const toolbar = document.querySelector<HTMLElement>("[data-view-toolbar]");
      const headerBottom = document.querySelector("header")?.getBoundingClientRect().bottom ?? 0;
      const visible = Boolean(toolbar)
        && toolbar!.getBoundingClientRect().top <= headerBottom;
      setCompactViewSwitcherVisible(visible);
      if (!visible) setCompactViewMenuOpen(false);
    };
    const scheduleCompactViewUpdate = () => {
      if (compactViewFrame !== undefined) return;
      compactViewFrame = window.requestAnimationFrame(updateCompactViewSwitcher);
    };
    const closeSearchOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSearchPanel(null);
      if (event.key === "Escape") popPreviewTarget();
      if (event.key === "Escape") setCompactViewMenuOpen(false);
    };
    window.addEventListener("keydown", closeSearchOnEscape);
    const onPopState = () => {
      setRoute(routeFromLocation());
      setSearchPanel(null);
      setDrawerOpen(false);
      setFriendRegistrationOpen(false);
      setEditingFriend(null);
      setCompactViewSwitcherVisible(false);
      setCompactViewMenuOpen(false);
      setListScrolled(false);
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("scroll", scheduleCompactViewUpdate, { passive: true });
    window.addEventListener("resize", scheduleCompactViewUpdate);
    scheduleCompactViewUpdate();
    onCleanup(() => {
      window.removeEventListener("keydown", closeSearchOnEscape);
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("scroll", scheduleCompactViewUpdate);
      window.removeEventListener("resize", scheduleCompactViewUpdate);
      if (compactViewFrame !== undefined) window.cancelAnimationFrame(compactViewFrame);
    });
  });

  const navigate = (nextRoute: AppRoute) => {
    if (nextRoute === route()) return;
    window.history.pushState({}, "", routePath(nextRoute));
    setRoute(nextRoute);
    setSearchPanel(null);
    setDrawerOpen(false);
    setFriendRegistrationOpen(false);
    setEditingFriend(null);
    setCompactViewSwitcherVisible(false);
    setCompactViewMenuOpen(false);
    setListScrolled(false);
  };

  const entries = () => result()?.entries ?? [];
  const entryByItemKey = createMemo(() => new Map(entries().flatMap((entry) =>
    entry.items.map((item) => [ownershipItemKey(item), entry] as const)
  )));
  createEffect(() => {
    const currentItems = new Map(entries().flatMap((entry) => entry.items).map((item) => [`${item.id}\n${item.url}`, item]));
    setSelection((current) => Object.fromEntries(
      GROUP_ORDER.map((group) => [group, current[group].map((item) => currentItems.get(`${item.id}\n${item.url}`) ?? item)])
    ) as Selection);
  });
  const mixEntryResult = createMemo(() => buildMixEntries(mixes() ?? [], entries()));
  const displayedEntries = createMemo(() => {
    if (route() === "mixes") return mixEntryResult().entries;
    if (route() !== "catalog" || !preferences().hideUnavailablePrimagi) return entries();
    return entries().filter((entry) => !(entry.series === "primagi" && entry.available === false));
  });
  const facets = createMemo(() => buildFacets(displayedEntries()));
  const brandOptions = createMemo(() => [...new Set(entries().map((entry) => entry.brand).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right, "ja")));
  const newestCategory = createMemo(() => latestCategory(entries()));
  createEffect(() => {
    const newest = newestCategory();
    if (!defaultCategoryApplied() && newest) {
      setCategories(new Set([newest]));
      setDefaultCategoryApplied(true);
    }
  });
  const normallyFiltered = createMemo(() => displayedEntries().filter((entry) =>
      matchesEntry(
        entry,
        {
          query: query(),
          brands: brands(),
          terms: terms(),
          categories: route() === "mixes" ? new Set<string>() : categories(),
        },
        colorFilters()
      )
    ));
  const filtered = createMemo(() => {
    const sorted = sortEntriesByNewestCategory(normallyFiltered());
    if (colorFiltersActive(colorFilters())) {
      return sorted
        .map((entry, index) => ({ entry, index, score: colorMatchScore(entry, colorFilters()) }))
        .sort((left, right) => left.score - right.score || left.index - right.index)
        .map(({ entry }) => entry);
    }
    return route() === "catalog" && randomOrder()
      ? randomizeEntries(sorted, randomSeed())
      : sorted;
  });
  const selectedItems = createMemo(() => GROUP_ORDER.flatMap((group) => selection()[group]));
  const registerMix = (registration: MixCoordinateRegistration) => {
    sendInBackground(
      `ミックスコーデ「${registration.name.trim()}」の登録`,
      () => postMixCoordinate(dataSource(), registration),
      { onSuccess: (added) => mutateMixes((current) => [...(current ?? []), added]) },
    );
  };
  const friendCoordinateOptions = createMemo<FriendCoordinateOption[]>(() => [
    ...entries().map((entry) => ({
      type: "existing" as const,
      id: catalogCoordinateId(entry),
      name: `${entry.name}（${categoryDisplayName(entry.category)}）`,
      items: entry.items,
    })),
    ...mixEntryResult().entries.flatMap((entry) => entry.mix ? [{
      type: "mixed" as const,
      id: entry.mix.id,
      name: `${entry.name}（ミックス）`,
      items: entry.items,
    }] : []),
  ]);
  const friendListItems = createMemo<FriendCodeListItem[]>((previous = []) => {
    const next = buildFriendCodeListItems(friendCodes() ?? [], entries(), mixes() ?? []);
    const previousByKey = new Map(previous.map((record) => [record.key, record]));
    return next.map((record) => {
      const prior = previousByKey.get(record.key);
      return prior?.code === record.code && prior.entry === record.entry ? prior : record;
    });
  }, []);
  const filteredFriendListItems = createMemo(() => {
    const query = friendQuery().normalize("NFKC").trim().toLowerCase();
    if (!query) return friendListItems();
    return friendListItems().filter((record) =>
      [record.playerName, record.playerId].some((value) =>
        value.normalize("NFKC").toLowerCase().includes(query)),
    );
  });
  createEffect(() => {
    route();
    preferences().viewMode;
    preferences().gridColumns;
    if (route() === "friends") filteredFriendListItems().length;
    else filtered().length;

    setDomPending(true);
    let settledFrame: number | undefined;
    const renderedFrame = window.requestAnimationFrame(() => {
      settledFrame = window.requestAnimationFrame(() => setDomPending(false));
    });
    onCleanup(() => {
      window.cancelAnimationFrame(renderedFrame);
      if (settledFrame !== undefined) window.cancelAnimationFrame(settledFrame);
    });
  });
  const relatedFriendsForEntry = (entry: CoordEntry): ImagePreviewTarget["relatedFriends"] => {
    const reference = entry.mix
      ? { type: "mixed" as const, id: entry.mix.id }
      : { type: "existing" as const, id: catalogCoordinateId(entry) };
    return friendListItems()
      .filter((record) => record.coordinate?.type === reference.type && record.coordinate.id === reference.id)
      .map((record) => ({ key: record.key, name: record.playerName }));
  };
  const registerFriend = (registration: Parameters<typeof postFriendCode>[1]) => {
    sendInBackground(
      `フレンドコード「${registration.playerName.trim()}」の登録`,
      () => postFriendCode(dataSource(), registration),
      { onSuccess: (added) => mutateFriendCodes((current) => [...(current ?? []), added]) },
    );
  };
  const friendCoordinateRequests = new Map<string, symbol>();
  const saveFriendCoordinate = (code: FriendCode, coordinate: FriendCoordinateReference) => {
    const requestToken = Symbol(code.id);
    const previousCoordinate = code.coordinate;
    friendCoordinateRequests.set(code.id, requestToken);
    mutateFriendCodes((current) => (current ?? []).map((candidate) =>
      candidate.id === code.id ? { ...candidate, coordinate } : candidate
    ));
    sendInBackground(
      `フレンド「${code.playerName}」のコーデ紐づけ変更`,
      () => updateFriendCoordinate(dataSource(), code.id, coordinate),
      {
        onFailure: () => {
          if (friendCoordinateRequests.get(code.id) !== requestToken) return;
          mutateFriendCodes((current) => (current ?? []).map((candidate) =>
            candidate.id === code.id ? { ...candidate, coordinate: previousCoordinate } : candidate
          ));
        },
        onSettled: () => {
          if (friendCoordinateRequests.get(code.id) === requestToken) friendCoordinateRequests.delete(code.id);
        },
      },
    );
  };

  const isOwnershipPending = (entry: CoordEntry, item: CoordItem) => {
    const row = findCatalogListRow(entry, item);
    return (row?.items ?? [item]).some((rowItem) => pendingOwnershipItems().has(ownershipItemKey(rowItem)));
  };
  const setEntryRowOwned = (entryKey: string, targetItems: CoordItem[], owned: boolean) => {
    const targetKeys = new Set(targetItems.map(ownershipItemKey));
    mutate((current) => current ? {
      ...current,
      entries: current.entries.map((entry) => entry.key !== entryKey ? entry : {
        ...entry,
        items: entry.items.map((item) => targetKeys.has(ownershipItemKey(item)) ? { ...item, owned } : item),
      }),
    } : current);
  };
  const updateOwnership = (entry: CoordEntry, item: CoordItem, owned: boolean) => {
    if (!storageConfigured()) {
      reportSendFailure("所持状況の更新", new Error("設定からユーザーデータの保存先を指定してください。"));
      return;
    }
    const row = findCatalogListRow(entry, item);
    if (!row || isOwnershipPending(entry, item)) return;
    const requestIds = ownershipRequestIds(row);
    if (!requestIds.length) return;
    const pendingKeys = row.items.map(ownershipItemKey);
    const previousOwnership = new Map(row.items.map((rowItem) => [ownershipItemKey(rowItem), rowItem.owned]));
    const scrollPosition = preferences().viewMode === "grid"
      ? { left: window.scrollX, top: window.scrollY }
      : null;
    setPendingOwnershipItems((current) => new Set([...current, ...pendingKeys]));
    setEntryRowOwned(entry.key, row.items, owned);
    if (scrollPosition) {
      queueMicrotask(() => restoreWindowScroll(scrollPosition.left, scrollPosition.top));
      window.requestAnimationFrame(() => restoreWindowScroll(scrollPosition.left, scrollPosition.top));
    }
    sendInBackground(
      `「${entry.name}」${row.label}の${owned ? "所持登録" : "所持解除"}`,
      () => Promise.all(requestIds.map((id) => postItemOwnership(dataSource(), id, owned))),
      {
        onFailure: () => mutate((current) => current ? {
          ...current,
          entries: current.entries.map((candidate) => candidate.key !== entry.key ? candidate : {
            ...candidate,
            items: candidate.items.map((candidateItem) => {
              const key = ownershipItemKey(candidateItem);
              return previousOwnership.has(key)
                ? { ...candidateItem, owned: previousOwnership.get(key) }
                : candidateItem;
            }),
          }),
        } : current),
        onSettled: () => setPendingOwnershipItems((current) => {
          const next = new Set(current);
          pendingKeys.forEach((key) => next.delete(key));
          return next;
        }),
      },
    );
  };
  const openQrRegistration = (entry: CoordEntry, item: CoordItem) => {
    const row = findCatalogListRow(entry, item);
    if (!row || !storageConfigured()) return;
    setQrRegistration({
      entry,
      item,
      modal: { title: catalogItemModalTitle(entry, item) },
    });
  };
  const showQrRow = (entry: CoordEntry, item: CoordItem, qrOverride?: string) => {
    const row = findCatalogListRow(entry, item);
    if (!row) return;
    const qr = qrOverride ?? row.qr;
    setQrDisplayTarget({
      title: catalogItemModalTitle(entry, item),
      items: [{ label: row.label, name: row.kind === "full" ? entry.name : item.name, id: row.id, qr }],
      onUpdate: storageConfigured() ? () => openQrRegistration(entry, item) : undefined,
    });
  };
  const showOrRegisterQr = (entry: CoordEntry, item: CoordItem) => {
    const row = findCatalogListRow(entry, item);
    if (!row) return;
    if (!row.qr) {
      openQrRegistration(entry, item);
      return;
    }
    showQrRow(entry, item);
  };
  const qrRequests = new Map<string, symbol>();
  const registerQr = (qr: string) => {
    const target = qrRegistration();
    if (!target) {
      reportSendFailure("QR登録", new Error("QR登録対象が見つかりません。"));
      return;
    }
    const row = findCatalogListRow(target.entry, target.item);
    if (!row) {
      reportSendFailure(`「${target.entry.name}」のQR登録`, new Error("QR登録対象が見つかりません。"));
      return;
    }
    const request = qrRequestTarget(row);
    if (!request) {
      reportSendFailure(`「${target.entry.name}」${row.label}のQR登録`, new Error("QR送信用IDを特定できません。"));
      return;
    }
    const requestKey = `${target.entry.key}\n${row.key}`;
    const requestToken = Symbol(requestKey);
    const previousQr = new Map(row.items.map((rowItem) => [ownershipItemKey(rowItem), {
      qr: rowItem.qr,
      setQR: rowItem.setQR,
    }]));
    qrRequests.set(requestKey, requestToken);
    const targetKeys = new Set(row.items.map(ownershipItemKey));
    mutate((current) => current ? {
      ...current,
      entries: current.entries.map((entry) => entry.key !== target.entry.key ? entry : {
        ...entry,
        items: entry.items.map((item) => {
          if (!targetKeys.has(ownershipItemKey(item))) return item;
          if (request.field === "setQR") return { ...item, setQR: qr };
          return item.id === request.id ? { ...item, qr } : item;
        }),
      }),
    } : current);
    showQrRow(target.entry, target.item, qr);
    setQrRegistration(null);
    sendInBackground(
      `「${target.entry.name}」${row.label}のQR登録`,
      () => postItemQr(dataSource(), request.id, qr),
      {
        onFailure: () => {
          if (qrRequests.get(requestKey) !== requestToken) return;
          mutate((current) => current ? {
            ...current,
            entries: current.entries.map((entry) => entry.key !== target.entry.key ? entry : {
              ...entry,
              items: entry.items.map((candidateItem) => {
                const previous = previousQr.get(ownershipItemKey(candidateItem));
                return previous ? { ...candidateItem, ...previous } : candidateItem;
              }),
            }),
          } : current);
        },
        onSettled: () => {
          if (qrRequests.get(requestKey) === requestToken) qrRequests.delete(requestKey);
        },
      },
    );
  };

  const entryForItem = (item: CoordItem) => entryByItemKey().get(ownershipItemKey(item));
  const previewItem = (item: CoordItem) => {
    const entry = entryForItem(item);
    const title = entry && item.name !== entry.name
      ? item.name
      : `${item.name} ${item.term}`;
    const status = entry ? catalogItemDisplayStatus(entry, item) : {
      owned: item.owned === true,
      qr: item.qr ?? "",
      qrLabel: item.term,
    };
    pushPreviewTarget({
      kind: "item",
      title,
      url: item.url,
      palette: item.colors?.palette ?? [],
      qrItems: itemQrVisible() && status.qr ? [{ label: status.qrLabel, name: item.name, id: item.id, qr: status.qr }] : [],
      metadata: [
        { label: "パーツ", value: item.term || "-" },
        { label: "★", value: entry?.star || "-" },
        { label: "ポイント", value: item.point || "-" },
        { label: "ブランド", value: item.brand || entry?.brand || "-" },
        { label: "カテゴリ", value: categoryDisplayName(item.category ?? entry?.category) },
        ...(entry?.series === "primagi" ? [{
          label: "使用可否",
          value: item.available === true ? "使用可能" : item.available === false ? "使用不可" : "不明",
        }] : []),
        { label: "ID", value: item.id || "-", wide: true },
      ],
    });
  };
  const coordinateQrItems = (entry: CoordEntry, includeUnowned: boolean): ImagePreviewTarget["qrItems"] => {
    if (!itemQrVisible()) return [];
    const rows = buildCatalogListRows(entry);
    const showUnowned = includeUnowned && rows.some((row) => Boolean(row.qr));
    return rows
      .filter((row) => showUnowned || isCatalogListRowOwned(row))
      .map((row) => {
        const owned = isCatalogListRowOwned(row);
        return {
          label: row.label,
          name: entry.name,
          id: row.id,
          qr: owned ? row.qr : "",
          missingLabel: owned ? "QR未登録" : "未所持",
        };
      });
  };
  const previewCoordinate = (entry: CoordEntry, options?: {
    title?: string;
    showImage?: boolean;
    showColors?: boolean;
    eyedropperEnabled?: boolean;
    qrItems?: ImagePreviewTarget["qrItems"];
    metadata?: NonNullable<ImagePreviewTarget["metadata"]>;
    relatedFriends?: ImagePreviewTarget["relatedFriends"];
    footerAction?: ImagePreviewTarget["footerAction"];
  }) => pushPreviewTarget({
    kind: "coordinate",
    title: options?.title ?? entry.name,
    url: entry.url,
    showImage: options?.showImage,
    showColors: options?.showColors,
    eyedropperEnabled: options?.eyedropperEnabled,
    palette: entry.colors?.palette ?? [],
    qrItems: options?.qrItems ?? coordinateQrItems(entry, true),
    metadata: options?.metadata ?? coordinateMetadata(entry),
    coordinateItems: entry.items,
    relatedFriends: options?.relatedFriends ?? relatedFriendsForEntry(entry),
    footerAction: options?.footerAction,
  });
  const previewFriend = (record: FriendCodeListItem, entry: CoordEntry) => previewCoordinate(entry, {
    title: `${record.playerName} / ${record.coordinateName}`,
    showImage: Boolean(record.code.imageUrl),
    showColors: false,
    eyedropperEnabled: false,
    qrItems: [{
      label: "フレンドQR",
      name: record.playerName,
      id: record.code.id,
      qr: record.code.qr,
    }],
    metadata: [
      ...coordinateMetadata(entry),
      { label: "プレイヤー", value: record.playerName },
      { label: "プレイヤーID", value: record.playerId || "-" },
      { label: "photo_seq", value: record.code.photoSeq || "-" },
      ...(record.code.tags.not_web ? [{ label: "種別", value: "webフレカではない" }] : []),
    ],
    relatedFriends: [],
    footerAction: {
      label: "コーデ紐づけ変更",
      onClick: () => setEditingFriend(record.code),
    },
  });
  const previewRelatedFriend = (key: string) => {
    const record = friendListItems().find((candidate) => candidate.key === key);
    if (record) previewFriend(record, friendDisplayEntry(record));
  };

  const groupFor = (item: CoordItem): CompareGroup | null => {
    const group = normalizeTerm(item.term);
    return GROUP_ORDER.includes(group as CompareGroup) ? (group as CompareGroup) : null;
  };
  const isSelected = (item: CoordItem) => {
    const group = groupFor(item);
    return !!group && selection()[group].some((selected) => selected.id === item.id && selected.url === item.url);
  };
  const toggleItem = (item: CoordItem) => {
    const group = groupFor(item);
    if (!group) return;
    setSelection((current) => {
      const exists = current[group].some((selected) => selected.id === item.id && selected.url === item.url);
      return {
        ...current,
        [group]: exists
          ? current[group].filter((selected) => selected.id !== item.id || selected.url !== item.url)
          : [...current[group], item],
      };
    });
  };
  const removeItem = (group: CompareGroup, item: CoordItem) =>
    setSelection((current) => ({
      ...current,
      [group]: current[group].filter((selected) => selected.id !== item.id || selected.url !== item.url),
    }));
  const resetNormal = () => {
    setQueryInput("");
    setQuery("");
    setBrands(new Set<string>());
    setTerms(new Set<string>());
    setCategories(new Set<string>());
    setRandomOrder(false);
  };
  const setViewMode = (viewMode: ViewMode) => {
    const currentMode = preferences().viewMode;
    const synchronize = (currentMode === "lookbook" && viewMode === "grid")
      || (currentMode === "grid" && viewMode === "lookbook");
    const anchorKey = synchronize ? visibleCatalogEntryKey() : null;
    setListScrolled(false);
    setPreferences((current) => saveViewPreferences({ ...current, viewMode }));
    if (anchorKey) {
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => scrollToCatalogEntry(anchorKey)));
    }
  };
  const quickFilterCategories = (filter: CatalogQuickFilter) => categoriesForCatalogQuickFilter(
    facets().categories.map(({ value }) => value),
    filter,
    newestCategory(),
  );
  const isQuickFilterActive = (filter: CatalogQuickFilter) => {
    const targets = quickFilterCategories(filter);
    return targets.length > 0
      && categories().size === targets.length
      && targets.every((category) => categories().has(category));
  };
  const toggleQuickFilter = (filter: CatalogQuickFilter) => {
    const targets = quickFilterCategories(filter);
    if (!targets.length) return;
    setCategories(isQuickFilterActive(filter) ? new Set<string>() : new Set(targets));
  };
  const addBrandFilter = (brand: string) => {
    if (!brand) return;
    setBrands((current) => new Set([...current, brand]));
  };
  const toggleRandomOrder = () => {
    if (!randomOrder()) setRandomSeed(Date.now());
    setRandomOrder((current) => !current);
  };
  const viewModeSwitcher = () => (
    <div class={styles.viewSwitcher} role="group" aria-label="コーデ一覧の表示形式">
      <button type="button" aria-pressed={preferences().viewMode === "lookbook"} onClick={() => setViewMode("lookbook")}>ルックブック</button>
      <button type="button" aria-pressed={preferences().viewMode === "grid"} onClick={() => setViewMode("grid")}>カタログ</button>
      <button type="button" aria-pressed={preferences().viewMode === "list"} onClick={() => setViewMode("list")}>リスト</button>
    </div>
  );
  const viewToolbar = () => (
    <div
      class={`${styles.catalogToolbar}${route() !== "catalog" ? ` ${styles.viewOnlyToolbar}` : ""}${preferences().viewMode === "list" ? ` ${styles.catalogToolbarList}` : ""}`}
      data-view-toolbar
    >
      <Show when={route() === "catalog"}>
        <div class={styles.viewSwitcher} role="group" aria-label="コーデのクイックフィルターと並び順">
          <button type="button" aria-pressed={isQuickFilterActive("latest")} onClick={() => toggleQuickFilter("latest")}>最新弾</button>
          <button type="button" aria-pressed={isQuickFilterActive("oa")} onClick={() => toggleQuickFilter("oa")}>おねがい</button>
          <button type="button" aria-pressed={isQuickFilterActive("secret")} onClick={() => toggleQuickFilter("secret")}>ひみつ</button>
          <button type="button" aria-pressed={isQuickFilterActive("primagi")} onClick={() => toggleQuickFilter("primagi")}>プリマジ</button>
          <button type="button" aria-pressed={randomOrder()} onClick={toggleRandomOrder}>ランダム</button>
        </div>
      </Show>
      {viewModeSwitcher()}
    </div>
  );

  return (
    <div class={styles.app}>
      <header class={styles.header}>
        <div class={styles.headerInner}>
          <div class={styles.headerTopRow}>
            <h1 class={styles.heading}>AipriDB</h1>
            <div class={styles.headerActions}>
              <HelpPanel
                open={searchPanel() === "help"}
                onToggleOpen={() => setSearchPanel((current) => current === "help" ? null : "help")}
              />
              <SettingsPanel
                open={searchPanel() === "settings"}
                dataSource={dataSource()}
                preferences={preferences()}
                onToggleOpen={() => setSearchPanel((current) => current === "settings" ? null : "settings")}
                onPreferencesChange={(nextPreferences) => {
                  if (nextPreferences.viewMode !== preferences().viewMode) setViewMode(nextPreferences.viewMode);
                  else setPreferences(saveViewPreferences(nextPreferences));
                }}
                onSaveDataSource={(value) => {
                  const saved = saveDataSource(value);
                  if (JSON.stringify(saved) === JSON.stringify(dataSource())) {
                    void refetch();
                    void refetchMixes();
                    void refetchFriendCodes();
                  }
                  else setDataSource(saved);
                  setSearchPanel(null);
                }}
              />
              <button
                class={styles.headerButton}
                type="button"
                title="データを再読み込み"
                aria-label="データを再読み込み"
                onClick={() => {
                  setSearchPanel(null);
                  if (route() === "mixes") void refetchMixes();
                  if (route() === "friends") void refetchFriendCodes();
                  void refetch();
                }}
              >↻</button>
            </div>
          </div>
          <div class={styles.headerSearchRow}>
            <form class={styles.quickSearch} role="search" onSubmit={(event) => event.preventDefault()}>
              <input
                class={styles.quickSearchInput}
                type="search"
                value={queryInput()}
                placeholder="コーデ名を検索"
                aria-label="コーデ名を検索"
                onInput={(event) => setQueryInput(event.currentTarget.value)}
              />
              <Show when={queryInput()}>
                <button class={styles.quickSearchClear} type="button" title="コーデ名検索を解除" aria-label="コーデ名検索を解除" onClick={() => { setQueryInput(""); setQuery(""); }}>✕</button>
              </Show>
            </form>
            <FilterPanel
              open={searchPanel() === "data"}
              filteredCount={filtered().length}
              totalCount={displayedEntries().length}
              showCategories={route() !== "mixes"}
              brands={brands()}
              terms={terms()}
              categories={categories()}
              facets={facets()}
              onToggleBrand={(value) => setBrands((current) => toggleSet(current, value))}
              onToggleTerm={(value) => setTerms((current) => toggleSet(current, value))}
              onToggleCategory={(value) => setCategories((current) => toggleSet(current, value))}
              onReset={resetNormal}
              onToggleOpen={() => setSearchPanel((current) => current === "data" ? null : "data")}
            />
            <ColorPanel
              open={searchPanel() === "color"}
              filteredCount={filtered().length}
              totalCount={displayedEntries().length}
              filters={colorFilters()}
              onPatch={(patch) => setColorFilters((current) => ({ ...current, ...patch }))}
              onReset={() => setColorFilters(defaultColorFilters())}
              onToggleOpen={() => setSearchPanel((current) => current === "color" ? null : "color")}
            />
            <Show when={compactViewSwitcherVisible() || (preferences().viewMode === "list" && listScrolled())}>
              <div class={styles.compactViewRoot}>
                <button class={styles.compactViewButton} type="button" title="表示形式を変更" aria-label="表示形式を変更" aria-expanded={compactViewMenuOpen()} onClick={() => setCompactViewMenuOpen((current) => !current)}>📖</button>
                <Show when={compactViewMenuOpen()}>
                  <div class={styles.compactViewMenu} role="menu" aria-label="コーデ一覧の表示形式">
                    <button type="button" role="menuitemradio" aria-checked={preferences().viewMode === "lookbook"} onClick={() => { setViewMode("lookbook"); setCompactViewMenuOpen(false); }}>ルックブック</button>
                    <button type="button" role="menuitemradio" aria-checked={preferences().viewMode === "grid"} onClick={() => { setViewMode("grid"); setCompactViewMenuOpen(false); }}>カタログ</button>
                    <button type="button" role="menuitemradio" aria-checked={preferences().viewMode === "list"} onClick={() => { setViewMode("list"); setCompactViewMenuOpen(false); }}>リスト</button>
                  </div>
                </Show>
              </div>
            </Show>
            <Show when={brands().size > 0}>
              <button
                class={styles.headerButton}
                type="button"
                title="ブランドフィルターを解除"
                aria-label="ブランドフィルターを解除"
                onClick={() => setBrands(new Set<string>())}
              >🚫</button>
            </Show>
          </div>
          <Show when={colorFilters().hslColors.length > 0}>
            <div
              class={styles.headerSelectedColors}
              aria-label={`選択中の検索色 ${colorFilters().hslColors.length}色。色検索を開く`}
            >
              <button
                class={styles.headerSelectedColorsLabel}
                type="button"
                aria-label="色検索を開く"
                onClick={() => setSearchPanel("color")}
              >検索色</button>
              <span class={styles.headerSelectedColorChips}>
                <For each={colorFilters().hslColors}>{(color, index) => (
                  <button
                    class={styles.headerSelectedColorChip}
                    type="button"
                    style={{ "background-color": hslToHex(color) }}
                    title={pendingHeaderColorRemoval() === hslToHex(color)
                      ? `もう一度押して${hslToHex(color)}を削除`
                      : `${hslToHex(color)}の削除を選択`}
                    aria-label={pendingHeaderColorRemoval() === hslToHex(color)
                      ? `もう一度押して${hslToHex(color)}を検索色から削除`
                      : `${hslToHex(color)}の削除を選択`}
                    aria-pressed={pendingHeaderColorRemoval() === hslToHex(color)}
                    onClick={() => {
                      const hex = hslToHex(color);
                      if (pendingHeaderColorRemoval() !== hex) {
                        setPendingHeaderColorRemoval(hex);
                        return;
                      }
                      setPendingHeaderColorRemoval(null);
                      setColorFilters((current) => ({
                        ...current,
                        hslColors: current.hslColors.filter((_, currentIndex) => currentIndex !== index()),
                      }));
                    }}
                  >
                    <Show when={pendingHeaderColorRemoval() === hslToHex(color)}><span>×</span></Show>
                  </button>
                )}</For>
              </span>
            </div>
          </Show>
        </div>
        <Show when={route() === "friends"}>
          <form class={styles.friendSearch} role="search" aria-label="フレカ検索" onSubmit={(event) => event.preventDefault()}>
            <div class={styles.friendSearchField}>
              <input
                class={styles.quickSearchInput}
                type="search"
                value={friendQuery()}
                placeholder="キャラクター名・CharacterSeqを検索"
                aria-label="キャラクター名・CharacterSeqを検索"
                onInput={(event) => setFriendQuery(event.currentTarget.value)}
              />
              <Show when={friendQuery()}>
                <button class={styles.quickSearchClear} type="button" aria-label="フレカ検索を解除" onClick={() => setFriendQuery("")}>✕</button>
              </Show>
            </div>
            <span class={styles.count} role="status">{filteredFriendListItems().length} / {friendListItems().length}件</span>
          </form>
        </Show>
        <div
          class={styles.headerProgress}
          role="progressbar"
          aria-label="処理中"
          aria-hidden={!appBusy()}
          style={{ opacity: appBusy() ? "1" : "0" }}
        >
          <div class={styles.headerProgressBar} data-app-progress-bar />
        </div>
      </header>

      <Switch>
        <Match when={route() === "mixes"}>
          <MixCoordinateList
          entries={filtered()}
          loading={mixes.loading}
          error={mixes.error}
          missingItemIds={mixEntryResult().missingItemIds}
          viewMode={preferences().viewMode}
          gridColumns={preferences().gridColumns}
          canUpdateOwnership={storageConfigured()}
          showItemQr={itemQrVisible()}
          qrCycleIntervalMs={preferences().qrCycleSeconds * 1_000}
          isSelected={isSelected}
          onToggle={toggleItem}
          onPreviewItem={previewItem}
          onPreviewEntry={(entry) => previewCoordinate(entry, {
            showImage: false,
            qrItems: coordinateQrItems(entry, false),
          })}
          isOwnershipPending={(item) => {
            const entry = entryForItem(item);
            return entry ? isOwnershipPending(entry, item) : false;
          }}
          onOwnershipChange={(item, owned) => {
            const entry = entryForItem(item);
            if (entry) void updateOwnership(entry, item, owned);
          }}
          onQrOpen={(item) => {
            const entry = entryForItem(item);
            if (entry) showOrRegisterQr(entry, item);
          }}
          onBrandFilter={addBrandFilter}
          onScrollPositionChange={setListScrolled}
          toolbar={viewToolbar()}
          colorFilters={colorFilters()}
          />
        </Match>
        <Match when={route() === "friends"}>
          <FriendCodeList
            items={filteredFriendListItems()}
            totalCount={friendListItems().length}
            loading={friendCodes.loading}
            error={friendCodes.error}
            canRegister={storageConfigured()}
            viewMode={preferences().viewMode}
            gridColumns={preferences().gridColumns}
            showItemQr={itemQrVisible()}
            qrCycleIntervalMs={preferences().qrCycleSeconds * 1_000}
            isSelected={isSelected}
            onToggle={toggleItem}
            onRegister={() => setFriendRegistrationOpen(true)}
            onEdit={setEditingFriend}
            onPreviewItem={previewItem}
            onPreviewRecord={previewFriend}
            isOwnershipPending={(item) => {
              const entry = entryForItem(item);
              return entry ? isOwnershipPending(entry, item) : false;
            }}
            onOwnershipChange={(item, owned) => {
              const entry = entryForItem(item);
              if (entry) void updateOwnership(entry, item, owned);
            }}
            onQrOpen={(item) => {
              const entry = entryForItem(item);
              if (entry) showOrRegisterQr(entry, item);
            }}
            onScrollPositionChange={setListScrolled}
            toolbar={viewToolbar()}
            colorFilters={colorFilters()}
          />
        </Match>
        <Match when={route() === "catalog"}>
      <main class={preferences().viewMode === "list" ? styles.mainList : styles.main} onPointerDown={() => { setSearchPanel(null); setCompactViewMenuOpen(false); }}>
        <Show when={preferences().viewMode !== "list"}>{viewToolbar()}</Show>
        <Show when={result()?.errors.length}>
          <div class={styles.alert} role="alert">
            一部のデータを読み込めませんでした。
            <ul><For each={result()?.errors}>{(error) => <li>{error}</li>}</For></ul>
          </div>
        </Show>
        <Show when={!result.loading && filtered().length === 0}>
          <p class={styles.empty}>条件に一致するコーデがありません。検索条件を変更してください。</p>
        </Show>
        <Switch>
          <Match when={preferences().viewMode === "list"}>
            <CoordList
            entries={filtered()}
            isSelected={isSelected}
            onToggle={toggleItem}
            canUpdateOwnership={storageConfigured()}
            showItemQr={itemQrVisible()}
            qrCycleIntervalMs={preferences().qrCycleSeconds * 1_000}
            isOwnershipPending={(item) => {
              const entry = entryForItem(item);
              return entry ? isOwnershipPending(entry, item) : false;
            }}
            onOwnershipChange={(item, owned) => {
              const entry = entryForItem(item);
              if (entry) void updateOwnership(entry, item, owned);
            }}
            onQrOpen={(item) => {
              const entry = entryForItem(item);
              if (entry) showOrRegisterQr(entry, item);
            }}
            onPreviewEntry={previewCoordinate}
            onScrollPositionChange={setListScrolled}
            onBrandFilter={addBrandFilter}
            plainBrand
            toolbar={viewToolbar()}
            colorFilters={colorFilters()}
            />
          </Match>
          <Match when={preferences().viewMode === "lookbook"}>
            <VirtualGrid
              items={filtered()}
              columns={preferences().gridColumns}
              mode="lookbook"
              class={styles.lookbookGrid}
              busy={result.loading}
              getKey={(entry) => entry.key}
            >
              {(entry) => (
                <LookbookCard
                  entry={entry}
                  showOwnership={storageConfigured()}
                  onPreview={() => previewCoordinate(entry)}
                  colorFilters={colorFilters()}
                />
              )}
            </VirtualGrid>
          </Match>
          <Match when={preferences().viewMode === "grid"}>
          <VirtualGrid
            items={filtered()}
            columns={preferences().gridColumns}
            mode="grid"
            class={styles.grid}
            busy={result.loading}
            getKey={(entry) => entry.key}
          >
            {(entry) => (
              <CoordCard
                entry={entry}
                isSelected={isSelected}
                onToggle={toggleItem}
                onPreviewItem={previewItem}
                canUpdateOwnership={storageConfigured()}
                showItemQr={itemQrVisible()}
                isOwnershipPending={(item) => isOwnershipPending(entry, item)}
                onOwnershipChange={(item, owned) => void updateOwnership(entry, item, owned)}
                onQrRegistration={(item) => showOrRegisterQr(entry, item)}
                onPreviewEntry={previewCoordinate}
                onBrandFilter={addBrandFilter}
                plainBrand
                colorFilters={colorFilters()}
              />
            )}
          </VirtualGrid>
          </Match>
        </Switch>
      </main>
        </Match>
      </Switch>

      <div class={styles.selectionControls}>
        <Show when={selectedItems().length}>
          <div class={styles.selectedThumbs} aria-label="選択中のコーデ">
            <For each={selectedItems()}>
              {(item) => (
                <button type="button" title={`${item.term}: ${item.name}`} onClick={() => previewItem(item)}>
                  <img src={imageUrl(item.url)} alt={`${item.term} ${item.name}`} loading="lazy" decoding="async" />
                </button>
              )}
            </For>
          </div>
        </Show>
        <button class={styles.fab} type="button" title="選択コーデ確認" aria-label="選択コーデ確認" onClick={() => setDrawerOpen(true)} aria-haspopup="dialog">
          🗒
        </button>
      </div>
      <CompareDrawer
        open={drawerOpen()}
        entries={entries()}
        selection={selection()}
        showQr={itemQrVisible()}
        qrCycleIntervalMs={preferences().qrCycleSeconds * 1_000}
        canRegisterMix={storageConfigured()}
        brandOptions={brandOptions()}
        onClose={() => setDrawerOpen(false)}
        onClear={() => setSelection(emptySelection())}
        onRemove={removeItem}
        onRegisterMix={registerMix}
      />
      <nav class={styles.bottomNav} aria-label="一覧切り替え">
        <button class={styles.bottomNavButton} type="button" aria-current={route() === "catalog" ? "page" : undefined} onClick={() => navigate("catalog")}>コーデ</button>
        <button class={styles.bottomNavButton} type="button" aria-current={route() === "mixes" ? "page" : undefined} onClick={() => navigate("mixes")}>ミックス</button>
        <button class={styles.bottomNavButton} type="button" aria-current={route() === "friends" ? "page" : undefined} onClick={() => navigate("friends")}>フレンド</button>
      </nav>
      <For each={previewTargets()}>{(target, index) => (
        <ImagePreviewModal
          target={target}
          selectedSearchColors={colorFilters().hslColors}
          qrCycleIntervalMs={preferences().qrCycleSeconds * 1_000}
          onClose={() => setPreviewTargets((current) => current.slice(0, index()))}
          onPreviewItem={previewItem}
          onPreviewFriend={previewRelatedFriend}
          onAddSearchColor={(swatch) => {
            const [hue, saturation, lightness] = swatch.hsl;
            setColorFilters((current) => ({
              ...current,
              hslColors: appendUniqueColors(current.hslColors, [{ hue, saturation, lightness }]),
            }));
          }}
        />
      )}</For>
      <QrModal
        target={qrDisplayTarget()}
        cycleIntervalMs={preferences().qrCycleSeconds * 1_000}
        onClose={() => setQrDisplayTarget(null)}
      />
      <QrRegistrationModal
        target={qrRegistration()?.modal ?? null}
        onClose={() => setQrRegistration(null)}
        onRegister={registerQr}
      />
      <FriendRegistrationModal
        open={friendRegistrationOpen()}
        options={friendCoordinateOptions()}
        onClose={() => setFriendRegistrationOpen(false)}
        onRegister={registerFriend}
      />
      <FriendCoordinateEditor
        code={editingFriend()}
        options={friendCoordinateOptions()}
        onClose={() => setEditingFriend(null)}
        onSave={saveFriendCoordinate}
      />
      <SendFailureModal
        target={sendFailures()[0] ?? null}
        remaining={Math.max(0, sendFailures().length - 1)}
        onClose={() => setSendFailures((current) => current.slice(1))}
      />
    </div>
  );
}
